import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "antd";
import { ColumnProps } from "antd/lib/table";
import { modal } from "Components/General/Modal/AntdModal";
import { dayjs } from "Config/dayjs";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { Table } from "Components/General/Table";
import { observer } from "mobx-react";

interface UserActiveTokensProps {
  userId: string | number;
  unifiedAccount: boolean;
}

export const UserActiveTokens: React.FC<UserActiveTokensProps> = observer(({ userId, unifiedAccount }) => {
  const { t } = useTranslation(I18N_NS.USER_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<API_RememberMeToken[]>([]);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      let url: string;
      if (unifiedAccount) {
        url = API_URL.PAVE_USERS.getPaveUserJwts(userId);
      } else {
        url = API_URL.AUTHENTICATIONS.getUserJwts(userId);
      }
      const response = await API.getData(url);
      setData(response);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleConfirmDelete = useCallback(
    (tokenId: number) => {
      const onOk = async () => {
        try {
          let url: string;
          if (unifiedAccount) {
            url = API_URL.AUTHENTICATIONS.deletePAVEUserJwt(userId, tokenId);
          } else {
            url = API_URL.AUTHENTICATIONS.deleteJwt(userId, tokenId);
          }
          await API.deleteData(url);
          fetch();
        } catch (error) {
          throw error;
        } finally {
          // eslint-disable-next-line no-unsafe-finally
          return Promise.resolve();
        }
      };
      modal.confirm({
        title: t("Delete authentication?"),
        onOk,
        icon: <ExclamationCircleOutlined />,
        okText: t("Yes", { ns: I18N_NS.GENERAL }),
        okType: "default",
        cancelButtonProps: { type: "primary" },
        cancelText: t("No", { ns: I18N_NS.GENERAL }),
        zIndex: 1052,
      });
    },
    [userId, fetch, t, unifiedAccount]
  );

  const columns: ColumnProps<API_RememberMeToken>[] = [
    {
      dataIndex: "name",
      title: t("Device"),
    },
    {
      dataIndex: "jti",
      title: t("Name"),
    },
    {
      dataIndex: "createdAt",
      title: t("Created"),
      render: (_, rec) => dayjs(rec.createdAt).format("lll"),
    },
    {
      dataIndex: "validUntil",
      title: t("Expires at"),
      render: (_, rec) => dayjs(rec.validUntil).format("lll"),
    },
    {
      dataIndex: "id",
      title: t("Actions"),
      width: 110,
      render: (_, rec) => (
        <Button size="small" type="link" onClick={() => handleConfirmDelete(rec.id)}>
          <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} />
        </Button>
      ),
    },
  ];

  return (
    <Table
      name="userActiveTokens"
      rowKey="id"
      columns={columns}
      loading={loading}
      dataSource={data}
      pagination={{ hideOnSinglePage: true }}
    />
  );
});
